import { useState } from 'react';
import { deleteSession, getSession } from '../../api';
import { Session } from '../../api/types';
import { Footer } from '../footer';
import { Header } from '../header';
import { MagicLinkWizard } from './magic-link-wizard';
import { LoadingScreen } from '../screens/loading';
import { ErrorScreen } from '../screens/error';
import { useAsyncEffect } from '../../hooks/use-async-effect';

/**
 * The main responsability of this component is to manage the session.
 * We remount the wizard when the session is mutated to reset it's internal
 * state and simplify state management. We do so explicitly to avoid relying
 * in hidden side effects.
 */
export const MagicLinkWizardSessionManager = () => {
  const [session, setSession] = useState<Session | null>(null);
  const [sessionFetchingState, setSessionFetchingState] = useState<ReqState>('in-progress');
  const [sessionClearingState, setSessionClearingState] = useState<ReqState>('idle');
  const [wizardRemountKey, setWizardRemountKey] = useState(0);

  useAsyncEffect(async () => await fetchSession(), []);

  async function fetchSession() {
    setSessionFetchingState('in-progress');
    try {
      const session = await getSession();
      const legalPersonsCountInSession = session?.legalPersons.length ?? 0;
      setSession(legalPersonsCountInSession > 0 ? session : null);
      setSessionFetchingState('idle');
      remountWizard();
    } catch (error) {
      console.error(error);
      setSessionFetchingState('error');
    }
  }

  async function clearSession() {
    setSessionClearingState('in-progress');
    try {
      await deleteSession();
      setSession(null);
      setSessionClearingState('idle');
      remountWizard();
    } catch (error) {
      console.error(error);
      setSessionClearingState('error');
    }
  }

  function remountWizard() {
    // react remounts the component when the key changes
    setWizardRemountKey((prev) => prev + 1);
  }

  if ([sessionFetchingState, sessionClearingState].some((s) => s === 'in-progress')) {
    return (
      <>
        <Header />
        <LoadingScreen message="Carregando..." />;
        <Footer />
      </>
    );
  }

  if ([sessionFetchingState, sessionClearingState].some((s) => s === 'error')) {
    const title = sessionFetchingState === 'error' ? 'Erro ao carregar a sessão' : 'Erro ao limpar a sessão';
    const refetch = sessionFetchingState === 'error' ? fetchSession : clearSession;
    const message =
      sessionFetchingState === 'error'
        ? 'Houve um problema para carregar a sessão.\nPor favor, tente novamente.'
        : 'Houve um problema para limpar a sessão.\nPor favor, tente novamente.';
    return (
      <>
        <Header />
        <ErrorScreen title={title} refetch={refetch} message={message} />;
        <Footer />
      </>
    );
  }

  return (
    <>
      <Header logout={session ? clearSession : undefined} />
      <MagicLinkWizard
        key={wizardRemountKey}
        session={session}
        clearSession={clearSession}
        refetchSession={fetchSession}
      />
      <Footer />
    </>
  );
};

type ReqState = 'idle' | 'in-progress' | 'error';
